import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { LivePlaysService, DefaultService } from '../generated-api';
import type { Player } from '../generated-api';
import PlayerAvatar from '../components/common/PlayerAvatar';
import { sendLivePlayMetadataUpdates } from '../utils/livePlayMetadata';

// Audio beep function using Web Audio API
const playBeep = () => {
  try {
    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = 800;
    oscillator.type = 'sine';
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
  } catch (err) {
    console.error('Error playing beep:', err);
  }
};

const TurnTimerPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const durationParam = searchParams.get('duration');
  const initialDuration = durationParam ? parseInt(durationParam, 10) : 60;

  const [timeRemaining, setTimeRemaining] = useState<number>(initialDuration);
  const [isRunning, setIsRunning] = useState<boolean>(true);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Team list state
  const [players, setPlayers] = useState<Player[]>([]);
  const [orderedTeams, setOrderedTeams] = useState<{ teamName: string; playerIds: string[] }[]>([]);
  const [currentTeamIndex, setCurrentTeamIndex] = useState<number>(0);
  const [teamTotalTimes, setTeamTotalTimes] = useState<Record<string, number>>({});
  const [currentTurnStartTime, setCurrentTurnStartTime] = useState<Date | null>(null);
  const [hasStarted, setHasStarted] = useState<boolean>(false);
  // timeUpdateCounter is used to trigger re-renders for live time updates
  const [, setTimeUpdateCounter] = useState<number>(0);
  // Track if we've already saved to prevent duplicate saves
  const hasSavedRef = useRef<boolean>(false);

  // Fetch live play data and extract player order
  useEffect(() => {
    const loadLivePlayData = async () => {
      if (!id) return;

      try {
        const [livePlayData, playersData] = await Promise.all([
          LivePlaysService.getLivePlayLivePlaysLivePlayIdGet({ livePlayId: id }),
          DefaultService.getPlayersPlayersGet(),
        ]);

        setPlayers(playersData);

        // Extract teams ordered by startingPosition
        // startingPosition is stored per team in currentState
        // Treat teams as units - all players from a team go together
        const teamsWithPositions: { teamName: string; position: number; playerIds: string[] }[] = [];
        let hasStartingPositions = false;

        if (livePlayData.currentState) {
          // Collect teams with their startingPosition
          Object.entries(livePlayData.currentState).forEach(([teamName, metadata]) => {
            if (metadata && typeof metadata === 'object' && 'startingPosition' in metadata) {
              const positionValue = metadata.startingPosition;
              // Handle both string and number values (backend may return strings)
              const position = typeof positionValue === 'string' 
                ? parseInt(positionValue, 10) 
                : typeof positionValue === 'number' 
                  ? positionValue 
                  : null;
              
              if (position !== null && !isNaN(position) && position > 0) {
                hasStartingPositions = true;
                // Get player IDs for this team
                const teamPlayerIds = livePlayData.teams[teamName] || [];
                if (teamPlayerIds.length > 0) {
                  teamsWithPositions.push({
                    teamName,
                    position,
                    playerIds: teamPlayerIds
                  });
                }
              }
            }
          });
        }

        // If we have starting positions, use them; otherwise, use all teams in order
        if (hasStartingPositions && teamsWithPositions.length > 0) {
          // Sort teams by startingPosition (ascending - lower position number = earlier turn)
          teamsWithPositions.sort((a, b) => a.position - b.position);
          
          // Store ordered teams for display
          const teams: { teamName: string; playerIds: string[] }[] = teamsWithPositions.map(t => ({
            teamName: t.teamName,
            playerIds: t.playerIds
          }));
          setOrderedTeams(teams);
          
          // Initialize total times from currentState if available, otherwise 0
          const initialTimes: Record<string, number> = {};
          teams.forEach(team => {
            const existingDuration = livePlayData.currentState?.[team.teamName]?.totalTurnDuration;
            // Handle both number and string values (backend may return strings)
            if (existingDuration !== undefined && existingDuration !== null) {
              initialTimes[team.teamName] = typeof existingDuration === 'string' 
                ? parseInt(existingDuration, 10) || 0
                : typeof existingDuration === 'number'
                  ? existingDuration
                  : 0;
            } else {
              initialTimes[team.teamName] = 0;
            }
          });
          setTeamTotalTimes(initialTimes);
        } else {
          // Fallback: use all teams in the order they appear
          const teams: { teamName: string; playerIds: string[] }[] = [];
          Object.entries(livePlayData.teams).forEach(([teamName, teamPlayerIds]) => {
            teams.push({ teamName, playerIds: teamPlayerIds });
          });
          if (teams.length > 0) {
            setOrderedTeams(teams);
            // Initialize total times from currentState if available, otherwise 0
            const initialTimes: Record<string, number> = {};
            teams.forEach(team => {
              const existingDuration = livePlayData.currentState?.[team.teamName]?.totalTurnDuration;
              // Handle both number and string values (backend may return strings)
              if (existingDuration !== undefined && existingDuration !== null) {
                initialTimes[team.teamName] = typeof existingDuration === 'string' 
                  ? parseInt(existingDuration, 10) || 0
                  : typeof existingDuration === 'number'
                    ? existingDuration
                    : 0;
              } else {
                initialTimes[team.teamName] = 0;
              }
            });
            setTeamTotalTimes(initialTimes);
          }
        }
      } catch (err) {
        console.error('Error loading live play data:', err);
      }
    };

    loadLivePlayData();
  }, [id]);

  // Initialize first team when timer starts
  useEffect(() => {
    if (orderedTeams.length > 0 && !hasStarted && isRunning) {
      setHasStarted(true);
      setCurrentTeamIndex(0);
      setCurrentTurnStartTime(new Date());
    }
  }, [orderedTeams.length, hasStarted, isRunning]);

  // Timer countdown logic
  useEffect(() => {
    if (isRunning && timeRemaining > 0) {
      intervalRef.current = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isRunning, timeRemaining]);

  // Handle timer reaching zero (expiration - don't advance player)
  useEffect(() => {
    if (timeRemaining === 0 && isRunning) {
      playBeep();
      setTimeRemaining(initialDuration);
      // Don't advance player on expiration, just restart countdown
    }
  }, [timeRemaining, isRunning, initialDuration]);

  // Force re-render every second to update live time display
  useEffect(() => {
    if (!currentTurnStartTime || orderedTeams.length === 0 || currentTeamIndex >= orderedTeams.length) {
      return;
    }

    const updateInterval = setInterval(() => {
      // Trigger a re-render to update live time display
      setTimeUpdateCounter(prev => prev + 1);
    }, 1000);

    return () => clearInterval(updateInterval);
  }, [currentTurnStartTime, orderedTeams.length, currentTeamIndex]);

  // Reset timer on tap (memoized to prevent re-renders)
  const resetTimer = useCallback(() => {
    if (orderedTeams.length > 0 && hasStarted) {
      // Save elapsed time for the current team
      if (currentTurnStartTime && currentTeamIndex < orderedTeams.length) {
        const now = new Date();
        const elapsedSeconds = Math.floor((now.getTime() - currentTurnStartTime.getTime()) / 1000);
        const currentTeam = orderedTeams[currentTeamIndex];
        
        // Update total time for the current team
        setTeamTotalTimes(prev => {
          const baseTime = prev[currentTeam.teamName] || 0;
          return {
            ...prev,
            [currentTeam.teamName]: baseTime + elapsedSeconds
          };
        });

        // Advance to next team
        const nextTeamIndex = (currentTeamIndex + 1) % orderedTeams.length;
        setCurrentTeamIndex(nextTeamIndex);
        setCurrentTurnStartTime(now);
      }
    }
    
    setTimeRemaining(initialDuration);
    setIsRunning(true);
  }, [initialDuration, orderedTeams, hasStarted, currentTurnStartTime, currentTeamIndex]);

  // Function to save team turn times by updating metadata directly
  const saveTeamTurnTimes = useCallback(async () => {
    // Prevent duplicate saves

    if (!id || !currentTurnStartTime) {
      return;
    }

    try {
      const now = new Date();
      const currentElapsedSeconds = Math.floor((now.getTime() - currentTurnStartTime.getTime()) / 1000);
      
      // Update current team's time before saving
      const finalTeamTimes = { ...teamTotalTimes };
      if (currentTeamIndex < orderedTeams.length) {
        const currentTeam = orderedTeams[currentTeamIndex];
        const baseTime = finalTeamTimes[currentTeam.teamName] || 0;
        finalTeamTimes[currentTeam.teamName] = baseTime + currentElapsedSeconds;
      }
      
      // Build metadata object with totalTurnDuration for all teams
      const metadata: Record<string, Record<string, any>> = {};
      for (const team of orderedTeams) {
        const teamTotalTime = finalTeamTimes[team.teamName] || 0;
        if (teamTotalTime > 0) {
          if (!metadata[team.teamName]) {
            metadata[team.teamName] = {};
          }
          metadata[team.teamName].totalTurnDuration = teamTotalTime;
        }
      }
      
      // Send metadata updates using shared utility function
      // This abstracts away the event creation from this component
      await sendLivePlayMetadataUpdates(id, metadata);
    } catch (err) {
      console.error('Error saving team turn times:', err);
      // Reset flag on error so we can retry if needed
      hasSavedRef.current = false;
    }
  }, [id, orderedTeams, currentTurnStartTime, currentTeamIndex, teamTotalTimes]);

  // Save team turn times when component unmounts
  useEffect(() => {
    return () => {
      // Cleanup function runs when component unmounts
      // Note: We can't await in cleanup, so we fire and forget
      // The hasSavedRef flag prevents duplicate saves
      if (id && orderedTeams.length > 0 && hasStarted && currentTurnStartTime) {
        saveTeamTurnTimes();
      }
    };
  }, [id, orderedTeams.length, hasStarted, currentTurnStartTime, saveTeamTurnTimes]);

  const handleBack = async (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Save team turn times before navigating away
    await saveTeamTurnTimes();
    
    navigate(`/live-plays/${id}`);
  };

  // Format time for display
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins > 0) {
      return `${mins}:${secs.toString().padStart(2, '0')}`;
    }
    return secs.toString();
  };

  // Format total time for player display
  const formatTotalTime = (totalSeconds: number): string => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Get current team's total time (including live elapsed time)
  const getCurrentTeamTotalTime = (teamName: string): number => {
    const baseTime = teamTotalTimes[teamName] || 0;
    if (currentTurnStartTime && currentTeamIndex < orderedTeams.length) {
      const currentTeam = orderedTeams[currentTeamIndex];
      // If this is the current team, add live elapsed time
      if (currentTeam && currentTeam.teamName === teamName) {
        const now = new Date();
        const elapsedSeconds = Math.floor((now.getTime() - currentTurnStartTime.getTime()) / 1000);
        return baseTime + elapsedSeconds;
      }
    }
    return baseTime;
  };

  // Calculate percentage for progress ring
  const percentage = (timeRemaining / initialDuration) * 100;
  const isTimeUp = timeRemaining === 0;

  // SVG circle calculations
  const size = 340;
  const strokeWidth = 20;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - percentage / 100);

  // Dynamic colors based on time remaining (matches background hue)
  const getProgressHue = () => Math.round((percentage / 100) * 120);

  const getProgressStyle = () => ({
    stroke: `hsl(${getProgressHue()}, 70%, 45%)`
  });

  const getGlowStyle = () => ({
    backgroundColor: `hsla(${getProgressHue()}, 60%, 70%, 0.5)`
  });

  // Calculate background color based on percentage (green → yellow → red)
  const getBackgroundStyle = () => {
    // Hue: 120 (green) at 100%, 60 (yellow) at 50%, 0 (red) at 0%
    const hue = Math.round((percentage / 100) * 120);
    const saturation = isTimeUp ? 70 : 45;
    const lightness = isTimeUp ? 85 : 92;

    return {
      background: `linear-gradient(135deg,
        hsl(${hue}, ${saturation}%, ${lightness}%) 0%,
        hsl(${hue}, ${saturation - 10}%, ${lightness + 3}%) 50%,
        hsl(${hue}, ${saturation}%, ${lightness}%) 100%)`
    };
  };

  return (
    <div
      className="fixed inset-0 flex flex-col items-center justify-center transition-all duration-500 cursor-pointer select-none"
      style={getBackgroundStyle()}
      onClick={resetTimer}
    >
      {/* Back Button */}
      <button
        onClick={handleBack}
        className="absolute top-20 left-6 z-10 flex items-center gap-2 px-4 py-2.5 bg-white/90 backdrop-blur-sm rounded-xl shadow-lg border border-edge-subtle text-body hover:bg-surface hover:shadow-xl transition-all duration-200 active:scale-95"
      >
        <ArrowLeftIcon className="h-5 w-5" />
        <span className="font-medium">Back</span>
      </button>

      {/* Main Timer Container */}
      <div className="flex flex-col items-center">
        {/* Header */}
        <div className="mb-8 text-center">
          <h2 className="text-sm font-bold text-disabled uppercase tracking-[0.2em] mb-1">Turn Timer</h2>
          <p className="text-xs text-disabled">Tap anywhere to reset</p>
        </div>

        {/* Circular Timer */}
        <div className="relative flex items-center justify-center">
          {/* Background glow effect */}
          <div
            className="absolute inset-0 rounded-full blur-3xl transition-all duration-500"
            style={{ transform: 'scale(0.85)', ...getGlowStyle() }}
          />

          {/* SVG Progress Ring */}
          <svg
            width={size}
            height={size}
            className="transform -rotate-90 relative z-10"
          >
            {/* Background circle */}
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke="currentColor"
              strokeWidth={strokeWidth}
              className="text-gray-200"
            />
            {/* Progress circle */}
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              strokeWidth={strokeWidth}
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              className="transition-all duration-1000 ease-linear"
              style={getProgressStyle()}
            />
          </svg>

          {/* Timer Display (centered in ring) */}
          <div className="absolute inset-0 flex flex-col items-center justify-center z-20">
            <span
              className={`font-mono font-bold tabular-nums transition-all duration-300 ${
                isTimeUp ? 'text-7xl animate-pulse' : 'text-8xl'
              }`}
              style={{ color: `hsl(${getProgressHue()}, 65%, 35%)` }}
            >
              {formatTime(timeRemaining)}
            </span>

            {/* Status indicator */}
            {isTimeUp ? (
              <span
                className="mt-2 text-lg font-bold uppercase tracking-wider animate-pulse"
                style={{ color: `hsl(${getProgressHue()}, 70%, 40%)` }}
              >
                Time's Up!
              </span>
            ) : (
              <span
                className="mt-2 text-xs font-medium uppercase tracking-widest"
                style={{ color: `hsl(${getProgressHue()}, 50%, 45%)` }}
              >
                {Math.ceil(percentage)}% remaining
              </span>
            )}
          </div>
        </div>

        {/* Bottom hint */}
        <div className="mt-12 flex items-center gap-2 px-4 py-2 bg-white/60 backdrop-blur-sm rounded-full border border-edge-subtle">
          <div
            className={`w-2 h-2 rounded-full ${isRunning && !isTimeUp ? 'animate-pulse' : ''}`}
            style={{ backgroundColor: isRunning && !isTimeUp ? `hsl(${getProgressHue()}, 70%, 45%)` : '#d1d5db' }}
          />
          <span className="text-xs font-medium text-muted uppercase tracking-wider">
            {isTimeUp ? 'Resetting...' : isRunning ? 'Running' : 'Paused'}
          </span>
        </div>
      </div>

      {/* Team List - shown if we have teams */}
      {orderedTeams.length > 0 && (
        <div className="absolute bottom-0 left-0 right-0 bg-white/95 backdrop-blur-md border-t border-edge shadow-lg">
          <div className="container mx-auto px-4 py-4">
            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
              {orderedTeams.map((team, teamIndex) => {
                const isCurrentTeam = teamIndex === currentTeamIndex && hasStarted;
                const teamTotalTime = getCurrentTeamTotalTime(team.teamName);

                return (
                  <div
                    key={team.teamName}
                    className={`flex-shrink-0 flex flex-col gap-2 px-4 py-3 rounded-xl transition-all duration-200 min-w-[140px] ${
                      isCurrentTeam
                        ? 'bg-primary-100 border-2 border-primary-500 shadow-md scale-105'
                        : 'bg-surface-base border-2 border-transparent'
                    }`}
                  >
                    {/* Team Name */}
                    <div className={`text-center text-xs font-semibold uppercase tracking-wide mb-1 ${
                      isCurrentTeam ? 'text-primary-700' : 'text-muted'
                    }`}>
                      {team.teamName}
                    </div>
                    
                    {/* Team Total Time */}
                    <div className={`text-center text-lg font-mono font-bold mb-2 ${
                      isCurrentTeam ? 'text-primary-700' : 'text-muted'
                    }`}>
                      {formatTotalTime(teamTotalTime)}
                    </div>
                    
                    {/* Players in Team (for display only) */}
                    <div className="flex gap-2 justify-center">
                      {team.playerIds.map((playerId) => {
                        const player = players.find(p => p.id === playerId);
                        // All players in the current team are active
                        const isActive = isCurrentTeam && hasStarted;

                        return (
                          <div
                            key={playerId}
                            className="flex flex-col items-center gap-1"
                          >
                            <div className={`relative ${isActive ? 'ring-2 ring-primary-500 rounded-full' : ''}`}>
                              <PlayerAvatar
                                name={player?.name || 'Player'}
                                imageUrl={player?.imageUrl}
                                size="small"
                              />
                            </div>
                            <div className={`text-xs font-medium text-center max-w-[60px] truncate ${
                              isActive ? 'text-primary-900' : 'text-body'
                            }`}>
                              {player?.name || 'Player'}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TurnTimerPage;
